import React, { useState, useEffect } from 'react';
import './PricingPlans.css';

const PricingPlans = ({ onPlanSelect, onFreePlanContinue }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedAccounts, setSelectedAccounts] = useState(2);
  const [calculatedPrice, setCalculatedPrice] = useState(200);

  // Pricing calculation for Scale plan
  const calculatePrice = (n) => {
    if (n >= 50) return 999.00; // Enterprise
    if (n === 1) return 99.00; // Single
    
    // Scale plan pricing - starts at $200 for 2 accounts to match settings
    // and gradually increases to $999 for 33+ accounts
    const basePrice = 200;
    const maxPrice = 999;
    const scaleFactor = (n - 2) / (33 - 2); // 0 to 1 scale
    const price = basePrice + (maxPrice - basePrice) * Math.pow(scaleFactor, 0.7);
    return Math.min(Math.round(price), 999);
  };

  // Calculate percentage savings compared to individual accounts
  const calculateSavings = (accounts) => {
    if (accounts <= 1) return 0;
    
    const pricePerAccount = calculatePrice(accounts) / accounts;
    const singleAccountPrice = 99; // Price for 1 account
    const savings = ((singleAccountPrice - pricePerAccount) / singleAccountPrice) * 100;
    return Math.max(0, Math.round(savings));
  };

  useEffect(() => {
    setCalculatedPrice(calculatePrice(selectedAccounts));
  }, [selectedAccounts]);

  const plans = [
    {
      id: 'single',
      name: 'Single',
      accounts: 1,
      price: 99,
      badge: '',
      features: [
        '✨ Unlimited video generation',
        '🎬 All video templates & styles', 
        '📱 Instagram posting integration',
        '🧵 Thread & slide generation',
        '💬 Email support',
        '🎯 Perfect for individual creators'
      ],
      buttonText: 'Get Started',
      buttonClass: 'plan-btn-single'
    },
    {
      id: 'scale',
      name: 'Scale',
      accounts: selectedAccounts,
      price: calculatedPrice,
      badge: selectedAccounts > 2 ? `-${calculateSavings(selectedAccounts)}%` : '',
      isPopular: true,
      hasSelector: true,
      features: [
        '✨ Unlimited video generation',
        '🎬 All video templates & styles',
        '📱 Instagram posting integration', 
        '🧵 Thread & slide generation',
        '🤖 Auto-mode capabilities',
        '💬 Priority support 24/7',
        '📈 Perfect for growing teams'
      ],
      buttonText: 'Most Popular',
      buttonClass: 'plan-btn-scale'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      accounts: 'Unlimited',
      price: 'Custom',
      badge: '',
      features: [
        '✨ Unlimited video generation',
        '🎬 All video templates & styles',
        '📱 Instagram posting integration',
        '🧵 Thread & slide generation', 
        '🤖 Advanced auto-mode capabilities',
        '🔥 Unlimited Twitter accounts',
        '👨‍💼 Dedicated account manager',
        '⚡ Premium support',
        '🏢 Perfect for large organizations'
      ],
      buttonText: 'Contact Sales',
      buttonClass: 'plan-btn-enterprise'
    }
  ];

  const handlePlanSelect = (plan) => {
    if (plan.id === 'enterprise') {
      // Handle enterprise contact sales differently
      window.open('mailto:sales@yourcompany.com?subject=Enterprise Plan Inquiry', '_blank');
      return;
    }

    const planData = {
      accounts: plan.id === 'single' ? 1 : selectedAccounts,
      price: plan.price,
      isUnlimited: plan.id === 'enterprise',
      planType: plan.id
    };
    onPlanSelect(planData);
  };

  const handleSliderChange = (e) => {
    const value = parseInt(e.target.value);
    setSelectedAccounts(value);
  };

  const getSliderBackground = () => {
    const percentage = ((selectedAccounts - 2) / (33 - 2)) * 100;
    return `linear-gradient(to right, #96ccff 0%, #96ccff ${percentage}%, #333333 ${percentage}%, #333333 100%)`;
  };

  return (
    <div className="pricing-plans-container">
      <div className="pricing-header">
        <h1>Choose Your Plan</h1>
        <p>Scale your video creation with our flexible pricing</p>
      </div>

      <div className="pricing-tabs-container">
        {plans.map((plan) => (
          <div 
            key={plan.id} 
            className={`pricing-tab ${plan.isPopular ? 'popular' : ''}`}
            onClick={() => setSelectedPlan(plan.id)}
          >
            {plan.isPopular && <div className="popular-badge">Most Popular</div>}
            {plan.badge && <div className="discount-badge">{plan.badge}</div>}
            
            <div className="plan-header">
              <h3 className="plan-name">{plan.name}</h3>
              <div className="plan-accounts">
                <span className="accounts-count">
                  {plan.hasSelector ? selectedAccounts : plan.accounts}
                </span>
                <span className="accounts-label">
                  {(plan.hasSelector ? selectedAccounts : plan.accounts) === 1 ? 'Account' : 'Accounts'}
                </span>
              </div>
            </div>

            <div className="plan-price">
              {plan.price === 'Custom' ? (
                <span className="price custom-price">{plan.price}</span>
              ) : (
                <>
                  <span className="currency">$</span>
                  <span className="price">{plan.price}</span>
                  <span className="period">per month</span>
                </>
              )}
            </div>

            <div className="plan-features">
              <ul>
                {plan.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>

            {plan.hasSelector && (
              <div className="account-selector">
                <div className="selector-header">
                  <span className="selector-label">Select Accounts (2-33)</span>
                  {selectedAccounts > 2 && (
                    <span className="savings-indicator">
                      Save {calculateSavings(selectedAccounts)}% vs individual plans
                    </span>
                  )}
                </div>
                <div className="slider-container">
                  <input
                    type="range"
                    min="2"
                    max="33"
                    value={selectedAccounts}
                    onChange={handleSliderChange}
                    className="account-slider"
                    style={{ background: getSliderBackground() }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="slider-labels">
                    <span>2</span>
                    <span>17</span>
                    <span>33</span>
                  </div>
                </div>
                {selectedAccounts > 1 && (
                  <div className="price-per-account">
                    ${Math.round(calculatedPrice / selectedAccounts)}/account/month
                    {selectedAccounts > 2 && (
                      <span className="vs-single"> (vs $99/account)</span>
                    )}
                  </div>
                )}
              </div>
            )}

            <button 
              className={`plan-button ${plan.buttonClass}`}
              onClick={(e) => {
                e.stopPropagation();
                handlePlanSelect(plan);
              }}
            >
              {plan.buttonText}
            </button>
          </div>
        ))}
      </div>

      <div className="free-plan-section">
        <div className="free-plan-card">
          <h3>Continue with Free Plan</h3>
          <div className="free-plan-details">
            <div className="free-limits">
              <span>🎥 10 videos per month</span>
              <span>👤 1 Twitter account only</span>
              <span>⚡ Basic features</span>
            </div>
          </div>
          <button className="free-plan-btn" onClick={onFreePlanContinue}>
            Continue Free
          </button>
        </div>
      </div>

      <div className="pricing-guarantee">
        <div className="guarantee-item">
          <span className="guarantee-icon">🔒</span>
          <span>Secure payment processing</span>
        </div>
        <div className="guarantee-item">
          <span className="guarantee-icon">↩️</span>
          <span>Cancel anytime</span>
        </div>
        <div className="guarantee-item">
          <span className="guarantee-icon">💳</span>
          <span>No hidden fees</span>
        </div>
      </div>
    </div>
  );
};

export default PricingPlans; 